import type { FaqPublic } from '@shared/schemas';
import { all, call, getContext, put, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { classifyInfraError } from '@/utils/infra-error';
import { logger } from '@/utils/logger';

import type { FaqGateway } from '../../_shared/infrastructure/faq.gateway';
import { clientFaqRequested, csmMessageSubmitted, searchMissed } from './client-faq.actions';
import { clientFaqActions } from './client-faq.slice';
import type { ClientFaqState } from './client-faq.state';

const DEFAULT_SEND_ERROR = 'Your message could not be sent. Please try again.';

/**
 * The CSM, or null when it cannot be read (a reader with no licence, a network
 * hiccup): the page then offers no contact, and the FAQ still shows.
 */
function* loadCsm(gateway: FaqGateway) {
  try {
    const csm: ClientFaqState['csm'] = yield call(gateway.loadCurrentCsm);
    return csm;
  } catch (e) {
    logger.warn('Could not read the CSM for the FAQ page', e);
    return null;
  }
}

/** WORKER: the published FAQ and the licence's CSM, together. */
function* loadWorker() {
  const gateway: FaqGateway = yield getContext('faqGateway');
  yield put(clientFaqActions.loadStarted());
  try {
    const [faq, csm] = (yield all([call(gateway.loadPublicFaq), call(loadCsm, gateway)])) as [
      FaqPublic,
      ClientFaqState['csm'],
    ];
    yield put(clientFaqActions.loadSucceeded({ categories: faq.categories, csm }));
  } catch {
    yield put(clientFaqActions.loadFailed());
  }
}

/** WORKER: mail the licence's CSM (#695). `takeLeading`: a double click sends once. */
function* sendCsmMessageWorker(action: ReturnType<typeof csmMessageSubmitted>) {
  const gateway: FaqGateway = yield getContext('faqGateway');
  yield put(clientFaqActions.csmMessageSending());
  try {
    yield call(gateway.sendCsmMessage, action.payload.message);
    yield put(clientFaqActions.csmMessageSent());
  } catch (e) {
    yield put(clientFaqActions.csmMessageFailed(classifyInfraError(e, DEFAULT_SEND_ERROR).message));
  }
}

/**
 * WORKER: report a search that found nothing (#735). Silent either way: the
 * client is not to be bothered by a report they never asked for.
 */
function* searchMissedWorker(action: ReturnType<typeof searchMissed>) {
  const gateway: FaqGateway = yield getContext('faqGateway');
  try {
    yield call(gateway.recordSearchMiss, action.payload.query);
  } catch (e) {
    logger.debug('Could not report an FAQ search with no result', e);
  }
}

/** WATCHERS. */
export function* clientFaqSaga() {
  yield all([
    takeLatest(clientFaqRequested.type, loadWorker),
    takeLeading(csmMessageSubmitted.type, sendCsmMessageWorker),
    takeEvery(searchMissed.type, searchMissedWorker),
  ]);
}
